/**
 * Generate QR Drawer
 *
 * Drawer for generating static QR codes linked to consent templates.
 * Two states: product selection (with checkboxes + division badges) and QR display.
 *
 * The QR encodes a static URL. No backend call at generate time.
 * Each scan of the QR triggers a fresh GMS session via the redirect endpoint.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Button,
} from 'equal-ds-ui';
import { StyledQRCode } from '../../ui/StyledQRCode';
import { Copy, Check, QrCode, ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import type { ProductRecord } from '../../../services/products-api.service';

export interface GenerateQRDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  products: ProductRecord[];
  multiConsentLimit: number;
  allowMultiConsent: boolean;
  /** Base URL for the QR redirect endpoint (from static_qr_config.qr_base_url) */
  qrBaseUrl: string;
}

type DrawerState = 'select' | 'display';

export const GenerateQRDrawer: React.FC<GenerateQRDrawerProps> = ({
  isOpen,
  onClose,
  products,
  multiConsentLimit,
  allowMultiConsent,
  qrBaseUrl,
}) => {
  const [drawerState, setDrawerState] = useState<DrawerState>('select');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');

  const { copied, copy } = useCopyToClipboard();

  // Reset state when drawer opens (matches CreateConsentDrawer pattern)
  useEffect(() => {
    if (isOpen) {
      setDrawerState('select');
      setSelectedProducts([]);
      setSearchQuery('');
      setGeneratedUrl('');
    }
  }, [isOpen]);

  // Filter products by search — products are already pre-filtered to active in ConsentRequestPage
  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.product_code.toLowerCase().includes(q) ||
          p.product_name.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [products, searchQuery]);

  // Selected product entries for chips and display
  const selectedProductEntries = useMemo(() => {
    return products
      .filter((p) => selectedProducts.includes(p.product_code))
      .map((p) => ({
        code: p.product_code,
        name: p.product_name,
      }));
  }, [products, selectedProducts]);

  const toggleProduct = useCallback(
    (productCode: string) => {
      setSelectedProducts((prev) => {
        if (prev.includes(productCode)) {
          return prev.filter((p) => p !== productCode);
        }
        if (allowMultiConsent && prev.length >= multiConsentLimit) {
          return prev;
        }
        if (!allowMultiConsent && prev.length >= 1) {
          return [productCode];
        }
        return [...prev, productCode];
      });
    },
    [allowMultiConsent, multiConsentLimit]
  );

  const handleGenerate = useCallback(() => {
    // Build static URL -- no backend call needed
    const productsParam = selectedProducts.join(',');
    const url = `${qrBaseUrl}/static-qr/redirect?products=${encodeURIComponent(productsParam)}`;
    setGeneratedUrl(url);
    setDrawerState('display');
  }, [selectedProducts, qrBaseUrl]);

  const handleReset = useCallback(() => {
    setDrawerState('select');
    setSelectedProducts([]);
    setGeneratedUrl('');
    setSearchQuery('');
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleDownloadQR = useCallback(() => {
    const svg = document.querySelector('#static-qr-canvas svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `static-qr-${selectedProducts.join('-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [selectedProducts]);

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="480px" className="bg-white shadow-2xl border border-border-default">
        {drawerState === 'select' ? (
          <>
            <DrawerHeader
              title="Generate Static QR"
              subtitle="Select consent templates to generate a reusable QR code. When scanned, users will be prompted to enter their mobile number and complete the consent journey."
              onClose={handleClose}
            />
            <DrawerContent className="p-6 space-y-4 overflow-y-auto">
              {/* Consent Template Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Consent Template Code
                </label>

                {/* Search */}
                <div className="relative mb-3">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchQuery(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                {/* Selected chips */}
                {selectedProducts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {selectedProductEntries.map((entry) => (
                      <span
                        key={entry.code}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                      >
                        {entry.code}
                        <button
                          onClick={() => toggleProduct(entry.code)}
                          className="hover:text-gray-900 ml-0.5"
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Product list with checkboxes */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-[360px] overflow-y-auto divide-y divide-gray-100">
                    {filteredProducts.length === 0 ? (
                      <p className="p-4 text-sm text-gray-400 text-center">No templates found</p>
                    ) : (
                      filteredProducts.map((p) => {
                        const isSelected = selectedProducts.includes(p.product_code);
                        const isDisabled =
                          !isSelected &&
                          allowMultiConsent &&
                          selectedProducts.length >= multiConsentLimit;
                        return (
                          <button
                            key={p.product_code}
                            onClick={() => toggleProduct(p.product_code)}
                            disabled={isDisabled}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                              isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                          >
                            {/* Checkbox */}
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected
                                ? 'bg-teal-600 border-teal-600'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <Check className="w-3.5 h-3.5 text-white" />
                              )}
                            </div>

                            {/* Product info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{p.product_code}</p>
                              <p className="text-xs text-gray-500">{p.product_name}</p>
                            </div>

                            {/* Division badge */}
                            {p.product_division && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                                {p.product_division}
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </DrawerContent>

            {/* Fixed bottom button */}
            <div className="p-4 border-t border-gray-200">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={selectedProducts.length === 0}
                onClick={handleGenerate}
              >
                <QrCode className="w-5 h-5" />
                Generate QR Code
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* QR Display State */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDrawerState('select')}
                  className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">Static QR Code</h2>
              </div>
              <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-md">
                <span className="text-gray-400 text-xl">x</span>
              </button>
            </div>

            <DrawerContent className="p-6 overflow-y-auto">
              <div className="flex flex-col items-center space-y-5">
                {/* QR Code */}
                <div id="static-qr-canvas" className="rounded-lg overflow-hidden">
                  <StyledQRCode
                    url={generatedUrl}
                    logoUrl="assets/images/finpro-f-logo.png"
                    size={240}
                    color="#1a3a4a"
                    bgColor="#ffffff"
                    logoSize={0.22}
                    dotsType="dots"
                    cornersSquareType="dot"
                    cornersDotType="dot"
                  />
                </div>

                {/* URL with copy */}
                <div className="w-full flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <p className="text-sm text-gray-600 truncate flex-1 font-mono">
                    {generatedUrl}
                  </p>
                  <button
                    onClick={() => copy(generatedUrl)}
                    className="p-1.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    title="Copy URL"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Selected templates */}
                <div className="w-full">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Selected Templates
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProductEntries.map((entry) => (
                      <span
                        key={entry.code}
                        className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200"
                      >
                        {entry.code} -- {entry.name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="w-full space-y-3 pt-2">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={() => copy(generatedUrl)}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    onClick={handleDownloadQR}
                  >
                    <Download className="w-4 h-4" />
                    Download QR
                  </Button>
                  <button
                    onClick={handleReset}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generate New
                  </button>
                </div>
              </div>
            </DrawerContent>
          </>
        )}
      </Drawer>
    </>
  );
};
